import { Ellipsis, type LucideIcon } from "lucide-react";
import type { Ref } from "react";

import { NARROW_ONLY, WIDE_ONLY } from "@/components/data-table";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { cn } from "@/lib/utils";

/** A row action's button: 36 px, and 45 px on a touch screen, so a finger finds it. */
const ROW_ACTION_SIZE = "h-8 w-8 pointer-coarse:size-10";

/** The width of an action's empty place in a row of icons. */
const ROW_ACTION_WIDTH = "w-8 pointer-coarse:w-10";

interface RowActionProps {
  /** What it does, for whom: the button's name and its tooltip ("gerd@… bewerken"). */
  label: string;
  icon: LucideIcon;
  onClick: () => void;
  /** A red icon, for deleting. Not for blocking: a red prohibition sign on every row reads as the person's state. */
  destructive?: boolean;
  disabled?: boolean;
  /**
   * Why the action cannot be taken for this row ("Nog nooit aangemeld in
   * Claude"): the button stays focusable but does nothing (`aria-disabled`),
   * and its tooltip says why under its name, also on keyboard focus. A
   * `disabled` button would get no tooltip at all.
   */
  disabledHint?: string;
  /** The button, for a caller that gives the focus back to it, such as after a cancelled question. */
  ref?: Ref<HTMLButtonElement>;
}

/**
 * An action at the end of a table row: a small ghost icon button that says
 * what it does in a themed tooltip, on hover and on keyboard focus.
 */
export function RowAction({
  label,
  icon: Icon,
  onClick,
  destructive,
  disabled,
  disabledHint,
  ref,
}: RowActionProps) {
  const unavailable = disabledHint !== undefined;
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <Button
          ref={ref}
          variant="ghost"
          size="icon"
          className={cn(
            ROW_ACTION_SIZE,
            unavailable && "cursor-not-allowed opacity-50 hover:bg-transparent",
          )}
          aria-label={label}
          aria-disabled={unavailable || undefined}
          onClick={unavailable ? undefined : onClick}
          disabled={disabled}
        >
          <Icon className={cn("h-4 w-4", destructive && "text-destructive")} aria-hidden="true" />
        </Button>
      </TooltipTrigger>
      <TooltipContent>
        {label}
        {unavailable && <span className="block opacity-80">{disabledHint}</span>}
      </TooltipContent>
    </Tooltip>
  );
}

/** One of a row's actions, for `<RowActions>`. */
export interface RowActionItem extends Omit<RowActionProps, "ref"> {
  /** What the phone's menu says: the action without its target ("Bewerken"). */
  short: string;
  /**
   * The action's place, when two actions take turns in it (Blokkeren and
   * Deblokkeren): the same button stays, so the focus it has stays too.
   */
  id?: string;
}

interface RowActionsProps {
  /** The menu button's name in the phone layout: "Acties voor gerd@…". */
  label: string;
  /**
   * The actions, in order. A `null` keeps an empty place in the row of icons,
   * so the icons of every row stay in their columns (a team that cannot be
   * deleted); the menu leaves it out.
   */
  actions: readonly (RowActionItem | null)[];
}

/**
 * A row's actions in a table: icons with tooltips in a wide table; in its
 * phone layout one menu button (…) that lists them with their names, since a
 * touch screen shows no tooltips. Destructive actions stay red in the menu,
 * and an action with a `disabledHint` is disabled there with its reason under
 * its name.
 */
export function RowActions({ label, actions }: RowActionsProps) {
  return (
    <>
      <div className={cn("flex items-end justify-end", WIDE_ONLY)}>
        {actions.map((action, index) =>
          action ? (
            <RowAction key={action.id ?? action.short} {...action} />
          ) : (
            <span key={`empty-${index}`} aria-hidden="true" className={ROW_ACTION_WIDTH} />
          ),
        )}
      </div>
      <div className={cn("flex justify-end", NARROW_ONLY)}>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" size="icon" aria-label={label}>
              <Ellipsis className="h-4 w-4" aria-hidden="true" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            {actions.map(
              (action) =>
                action && (
                  <DropdownMenuItem
                    key={action.id ?? action.short}
                    variant={action.destructive ? "destructive" : "default"}
                    disabled={action.disabled || action.disabledHint !== undefined}
                    onSelect={action.onClick}
                  >
                    <action.icon aria-hidden="true" />
                    {action.disabledHint === undefined ? (
                      action.short
                    ) : (
                      // A touch screen shows no tooltip: the reason goes under the name.
                      <span className="flex flex-col">
                        {action.short}
                        <span className="text-xs">{action.disabledHint}</span>
                      </span>
                    )}
                  </DropdownMenuItem>
                ),
            )}
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </>
  );
}
